/**
 * Hook to fetch facet counts for the current filter set.
 * Merges counts from Typesense with the full universe of options.
 */

import { useQuery } from '@tanstack/react-query';
import type { SearchResponseFacetCountSchema } from 'typesense/lib/Typesense/Documents';

import { useTypesenseAPIKey } from './useTypesenseAPIKey';
import { typesense } from '@/api/typesense/typesenseClient';
import type { FilterSet } from '@/lib/filter-dsl';
import type { ResourceConfig } from '@/lib/resource-config';
import { toFilterBy, toFacetBy, toQueryBy } from '@/lib/typesense-map';

interface UseFacetCountsParams {
  config: ResourceConfig;
  filterSet: FilterSet;
  /**
   * Optional facet key to exclude from the filter when calculating counts.
   * This allows showing counts for a facet independent of its own filter.
   * Useful when editing a filter to show "how many results if I add this value"
   * instead of "how many results match my current filter".
   */
  excludeFacet?: string;
  /**
   * Optional search query to filter results.
   * When provided, facet counts will show how many items match both the filters AND the search.
   * Defaults to '*' (wildcard) if not provided or empty.
   *
   * Example: If user searches for "Warehouse Project", facet counts will show:
   * - Customer A: 3 (3 warehouse projects for customer A)
   * - Customer B: 1 (1 warehouse project for customer B)
   * Instead of showing counts for ALL projects.
   */
  query?: string;
}

/**
 * Facet counts keyed by facetKey, then by value slug
 * Example: { customer: { maersk: 42, hapag: 18 }, status: { active: 30 } }
 */
export type FacetCounts = Record<string, Record<string, number>>;

/**
 * Fetches facet counts for the current filter set.
 * Returns counts for all facets with facetBy: true.
 *
 * @param params - Hook parameters
 * @returns React Query result with facet counts
 */
export function useFacetCounts({
  config,
  filterSet,
  excludeFacet,
  query,
}: UseFacetCountsParams) {
  const { apiKey } = useTypesenseAPIKey(config.typesenseCollection);

  // Default query to wildcard if not provided or empty
  const searchQuery = query ?? '*';

  // If excludeFacet is specified, remove that facet's clause from the filter
  const filteredSet = excludeFacet
    ? filterSet.filter((clause) => clause.facet !== excludeFacet)
    : filterSet;

  const filterBy = toFilterBy(config, filteredSet);
  const facetBy = toFacetBy(config);
  const queryBy = toQueryBy(config);

  const queryKey = [
    'facetCounts',
    config.resourceKey,
    config.typesenseCollection,
    filterBy,
    searchQuery,
    excludeFacet || 'all',
  ];

  return useQuery<FacetCounts>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- apiKey is tracked via config.typesenseCollection
    queryKey,
    queryFn: async () => {
      // Note: apiKey is implicitly tracked via config.typesenseCollection in queryKey
      if (!apiKey) {
        console.error('[useFacetCounts] API key not available');
        return {};
      }

      try {
        if (facetBy.length === 0) {
          // No facets to count
          return {};
        }

        const searchRequest = {
          searches: [
            {
              collection: config.typesenseCollection,
              'x-typesense-api-key': apiKey,
              q: searchQuery,
              query_by: queryBy,
              filter_by: filterBy,
              facet_by: facetBy.join(','),
              max_facet_values: 250,
              per_page: 0,
            },
          ],
        };

        const multiSearchResults =
          await typesense.multiSearch.perform(searchRequest);
        const result = multiSearchResults.results[0] as {
          facet_counts?: SearchResponseFacetCountSchema<
            Record<string, unknown>
          >[];
        };

        // Parse facet counts from Typesense response
        return parseFacetCounts(config, result.facet_counts || []);
      } catch (error) {
        console.error('[useFacetCounts] Error fetching counts:', error);
        return {};
      }
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 60 * 1000, // 1 minute
    enabled: !!apiKey,
  });
}

/**
 * Parses Typesense facet_counts response into our FacetCounts format.
 * Maps field names back to facetKeys.
 */
function parseFacetCounts(
  config: ResourceConfig,
  facetCountsResponse: SearchResponseFacetCountSchema<
    Record<string, unknown>
  >[],
): FacetCounts {
  const counts: FacetCounts = {};

  // Create reverse mapping: field -> facetKey (handles both field and facetField)
  const fieldToFacetKey: Record<string, string> = {};
  for (const [facetKey, facetConfig] of Object.entries(config.facets)) {
    // Use facetField if specified, otherwise use field
    const fieldName = facetConfig.facetField || facetConfig.field;
    fieldToFacetKey[fieldName] = facetKey;
  }

  for (const facetCount of facetCountsResponse) {
    const fieldName = facetCount.field_name;
    const facetKey = fieldToFacetKey[fieldName];

    if (!facetKey) continue;

    const valueCounts: Record<string, number> = {};

    for (const countItem of facetCount.counts || []) {
      // Trim whitespace from count values to handle data quality issues
      const value = String(countItem.value).trim();
      const count = countItem.count;
      valueCounts[value] = count;
    }

    counts[facetKey] = valueCounts;
  }

  return counts;
}
